import { useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { actualizarActivo } from "../../utils/activos";
import { hoyDelPais } from "../../utils/fechasFuturosPanel";
import { MODOS_VALIDACION } from "../../utils/validarFechaDocumento";

// Edita la fecha de proyección de pago (activos.fecha_planificada) de un CapEx planificado,
// directamente desde el panel "Movimientos Futuros" (hasta ahora solo se podía desde el modal
// Inversión del edificio). Escribe SOLO fecha_planificada — no toca fecha_compra ni el estado,
// así que no dispara depreciación ni saca el activo de la proyección: solo lo reubica en el tiempo.
// Reglas del campo: obligatoria (10.158) y sin fechas pasadas (es proyección a futuro; si el pago
// ya ocurrió, el camino es "Confirmar pago ejecutado").

const normalizarFecha = (fechaStr) => {
  if (!fechaStr) return "";
  return fechaStr.includes("T") ? fechaStr.substring(0, 10) : fechaStr;
};

const formatMonto = (entry) => {
  const n = Math.abs(Number(entry?.importeEnMoneda || 0));
  const num = n.toLocaleString("es-CL", { maximumFractionDigits: 0 });
  const moneda = entry?.moneda_origen === "USD" ? "US$ " : "";
  return `${moneda}${num}`;
};

const ModificarFechaProyeccionCapexModal = ({ show, onHide, entry, onSaved, pais }) => {
  const [fecha, setFecha] = useState(normalizarFecha(entry?.fecha_display));
  const [guardando, setGuardando] = useState(false);

  if (!entry) return null;

  // "Hoy" con el huso REAL del país operativo (`hoyDelPais`, Fase 1c) — reemplaza la constante de
  // módulo `new Date().toISOString().split("T")[0]` (huso del NAVEGADOR, evaluada UNA vez al cargar
  // el módulo, nunca se actualizaba y podía correrse de día contra Chile/Perú). Se recalcula en cada
  // render (no a nivel de módulo) porque depende del `pais` de la fila, una prop.
  const HOY = hoyDelPais(pais);

  const handleGuardar = async () => {
    if (!fecha) {
      toast.error("La fecha es obligatoria");
      return;
    }
    if (fecha < HOY) {
      toast.error("La fecha no puede ser anterior a hoy. Si el pago ya ocurrió, usá «Confirmar pago ejecutado».");
      return;
    }
    setGuardando(true);
    try {
      // F1 (B.2b, 2026-09-26): DATO_DOCUMENTO PROVISIONAL — este modal (uno de los "8 modales
      // viejos", permanentes por 10.232) ya valida piso acá arriba, pero NO día hábil; ESTRICTO
      // agregaría esa restricción nueva. pendiente Fase 5 (su propia migración, si corresponde).
      await actualizarActivo(entry.activo_id, { fecha_planificada: fecha }, { modo: MODOS_VALIDACION.DATO_DOCUMENTO });
      toast.success("Fecha de proyección actualizada");
      // Patch puntual (Fase 2A): el padre actualiza `capexEntries` por activo_id, sin refetch.
      onSaved?.(fecha);
    } catch (e) {
      toast.error(`Error al guardar: ${e.message || e}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Modificar fecha de proyección de pago</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ fontSize: "0.9rem", lineHeight: 1.7, marginBottom: 16 }}>
          <div>
            <strong>Concepto:</strong> {entry.razon_social_receptor || "—"}
          </div>
          <div>
            <strong>Monto:</strong> {formatMonto(entry)}
          </div>
        </div>

        <Form.Group>
          <Form.Label style={{ fontWeight: 600, fontSize: "0.9rem" }}>Fecha de proyección de pago</Form.Label>
          <Form.Control
            type="date"
            value={fecha}
            min={HOY}
            onChange={(e) => setFecha(e.target.value)}
            disabled={guardando}
            style={!fecha ? { borderColor: "#dc3545" } : undefined}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModificarFechaProyeccionCapexModal;
